import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { FrequencyType, OffsetCode } from '../../types/database';

interface OffsetCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: OffsetCodeFormData, id?: string) => void;
  isSaving: boolean;
  editingOffset?: OffsetCode | null;
}

export interface OffsetCodeFormData {
  code_number: string;
  name: string;
  frequency_type: FrequencyType;
}

const EMPTY_FORM: OffsetCodeFormData = {
  code_number: '',
  name: '',
  frequency_type: 'monthly',
};

export function OffsetCodeModal({
  isOpen,
  onClose,
  onSave,
  isSaving,
  editingOffset,
}: OffsetCodeModalProps) {
  const [formData, setFormData] = useState<OffsetCodeFormData>(EMPTY_FORM);

  // ── MUST be declared BEFORE useEffect so it is not in the TDZ when the
  //    effect callback runs. Using useCallback keeps the reference stable.
  const resetForm = useCallback(() => {
    setFormData(EMPTY_FORM);
  }, []);

  // Pre-fill when editing; clear when opening for a new entry
  useEffect(() => {
    if (editingOffset) {
      setFormData({
        code_number: editingOffset.code_number,
        name: editingOffset.name,
        frequency_type: editingOffset.frequency_type,
      });
    } else {
      resetForm();
    }
  }, [editingOffset, isOpen, resetForm]);

  if (!isOpen) return null;

  const isEditMode = !!editingOffset;

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData, editingOffset?.id);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditMode ? 'ערוך חוק קיזוז' : 'הוסף חוק קיזוז'}
            </h2>
            <button
              onClick={handleClose}
              disabled={isSaving}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col">
            <div className="p-6 space-y-6">
              {/* Base Fields */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                  פרטים בסיסיים
                </h3>

                {/* Offset Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    מספר קיזוז <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.code_number}
                    onChange={(e) => setFormData({ ...formData, code_number: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                    placeholder="לדוגמה: OFF-001"
                    required
                  />
                </div>

                {/* Offset Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    שם החוק <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                    placeholder="לדוגמה: קיזוז שכר חודשי"
                    required
                  />
                </div>
              </div>

              {/* Frequency Type */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                  חלון זמן להתאמה
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                    סוג תדירות <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.frequency_type}
                    onChange={(e) =>
                      setFormData({ ...formData, frequency_type: e.target.value as FrequencyType })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
                    required
                  >
                    <option value="daily">יומי</option>
                    <option value="weekly_5">שבועי (5 ימים)</option>
                    <option value="weekly_6">שבועי (6 ימים)</option>
                    <option value="weekly">שבועי</option>
                    <option value="monthly">חודשי</option>
                    <option value="quarterly">רבעוני</option>
                    <option value="semi_annually">חצי שנתי</option>
                    <option value="annually">שנתי</option>
                    <option value="custom">מותאם אישית</option>
                  </select>
                  <p className="mt-2 text-sm text-gray-500 text-right">
                    בחר את חלון הזמן להתאמה בין עסקאות צפויות לפועל
                  </p>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSaving}
                className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                ביטול
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-2.5 text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'שומר...' : isEditMode ? 'עדכן חוק קיזוז' : 'שמור חוק קיזוז'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
